import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, mediaUrl } from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { applySeo, applyNotFoundSeo, SITE_URL } from "@/lib/seo";
import { ArrowLeft, ArrowRight, Sparkles, Star } from "lucide-react";

const ArticleCard = ({ p, featured = false }) => (
  <Link
    to={`/articolo/${p.slug}`}
    data-testid={`hub-article-${p.slug}`}
    className="group flex flex-col transition-transform duration-300 ease-out hover:-translate-y-1.5"
  >
    <div className="overflow-hidden mb-5 relative bg-[#E7E5E4]">
      {p.cover_image ? (
        <img
          src={mediaUrl(p.cover_image)}
          alt={p.title}
          loading="lazy"
          className={`w-full object-cover transition-transform duration-700 group-hover:scale-[1.06] ${featured ? "aspect-[16/9]" : "aspect-[3/2]"}`}
        />
      ) : (
        <div className={`w-full ${featured ? "aspect-[16/9]" : "aspect-[3/2]"} flex items-center justify-center`}>
          <span className="font-display italic text-2xl text-[#A8A29E]">Giraffe</span>
        </div>
      )}
      <span className="pointer-events-none absolute inset-0 bg-[#1C1917] opacity-0 group-hover:opacity-[0.06] transition-opacity duration-300" />
    </div>
    <h3 className={`font-display leading-tight tracking-tight text-[#1C1917] group-hover:text-[#9F583F] transition-colors ${featured ? "text-3xl md:text-4xl" : "text-2xl"}`}>
      {p.title}
    </h3>
    {p.excerpt && (
      <p className="font-body text-[#57534E] mt-3 leading-relaxed line-clamp-3">{p.excerpt}</p>
    )}
    <span className="inline-flex items-center gap-2 mt-3 label-ui text-[#9F583F] opacity-0 -translate-x-1.5 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300">
      Leggi l'articolo <ArrowRight size={14} strokeWidth={1.6} />
    </span>
  </Link>
);

export default function CategoryHub() {
  const { slug } = useParams();
  const [hub, setHub] = useState(undefined); // undefined=loading, null=not found

  useEffect(() => {
    window.scrollTo(0, 0);
    setHub(undefined);
    api
      .get(`/categories/hub/${slug}`)
      .then((r) => setHub(r.data))
      .catch(() => setHub(null));
  }, [slug]);

  // SEO — self-referential canonical (never towards an article), unique title/desc.
  useEffect(() => {
    if (hub === undefined) return;
    const path = `/categoria/${slug}`;
    if (!hub) {
      // Not-found hub: same convention as deleted articles — noindex + NO canonical.
      applyNotFoundSeo(path);
      return;
    }
    const desc =
      (hub.intro && hub.intro.trim().slice(0, 300)) ||
      (hub.description && hub.description.trim()) ||
      `Tutti gli articoli di ${hub.name} su Il Cassetto delle Giraffe.`;
    const image = hub.articles?.[0]?.cover_image
      ? (/^https?:\/\//i.test(hub.articles[0].cover_image)
          ? hub.articles[0].cover_image
          : SITE_URL + hub.articles[0].cover_image)
      : undefined;
    const canonical = SITE_URL + path;
    applySeo({
      path,
      title: `${hub.name} — Il Cassetto delle Giraffe`,
      description: desc,
      image,
      type: "website",
      noindex: !hub.eligible, // thin hubs (< 5 articles) are not indexable
      jsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: `${hub.name} — Il Cassetto delle Giraffe`,
          description: desc,
          url: canonical,
          isPartOf: { "@type": "WebSite", name: "Il Cassetto delle Giraffe", url: SITE_URL },
        },
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL + "/" },
            { "@type": "ListItem", position: 2, name: "Giraffe Map", item: SITE_URL + "/giraffe-map" },
            { "@type": "ListItem", position: 3, name: hub.name, item: canonical },
          ],
        },
      ],
    });
  }, [hub, slug]);

  if (hub === undefined) {
    return (
      <div className="relative z-10 min-h-screen bg-[#F9F8F6]">
        <Header />
        <p className="max-w-3xl mx-auto px-6 py-40 font-ui text-sm text-[#57534E]">Caricamento…</p>
      </div>
    );
  }

  if (!hub) {
    return (
      <div className="relative z-10 min-h-screen bg-[#F9F8F6]">
        <Header />
        <div className="max-w-3xl mx-auto px-6 py-40 text-center">
          <h1 className="font-display text-5xl text-[#1C1917] italic">Categoria non trovata</h1>
          <Link to="/giraffe-map" className="inline-block mt-8 label-ui text-[#9F583F] link-underline">
            Vai alla Giraffe Map
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const articles = hub.articles || [];
  const featured = [...articles].sort((a, b) => (b.views || 0) - (a.views || 0))[0];
  const rest = articles.filter((a) => a.slug !== featured?.slug);
  const introText =
    (hub.intro && hub.intro.trim()) ||
    (hub.description && hub.description.trim()) ||
    `Benvenuto nella raccolta dedicata a ${hub.name}. Qui trovi tutti gli articoli del Cassetto delle Giraffe che esplorano questo tema, tra storie, curiosità e riflessioni.`;

  return (
    <div className="relative z-10 min-h-screen bg-[#F9F8F6]">
      <Header />

      <section className="max-w-4xl mx-auto px-6 md:px-10 pt-14 pb-6" data-testid="category-hub">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 label-ui text-[#A8A29E] mb-8" aria-label="breadcrumb">
          <Link to="/" className="hover:text-[#9F583F] transition-colors">Home</Link>
          <span>/</span>
          <Link to="/giraffe-map" className="hover:text-[#9F583F] transition-colors">Giraffe Map</Link>
          <span>/</span>
          <span className="text-[#57534E]">{hub.name}</span>
        </nav>

        <p className="inline-flex items-center gap-2 label-ui text-[#9F583F] mb-3">
          <Sparkles size={14} strokeWidth={1.6} /> Raccolta tematica
        </p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1] tracking-tight text-[#1C1917]" data-testid="hub-title">
          {hub.name}
        </h1>

        <div className="w-14 h-[3px] bg-[#9F583F] rounded-full mt-7 mb-6" />
        {introText.split("\n").filter(Boolean).map((para, i) => (
          <p key={i} className="font-body text-lg md:text-xl text-[#57534E] leading-relaxed mb-4 max-w-3xl">
            {para}
          </p>
        ))}
        <p className="label-ui text-[#A8A29E] mt-2">{articles.length} articol{articles.length === 1 ? "o" : "i"}</p>
      </section>

      <section className="max-w-6xl mx-auto px-6 md:px-10 pb-24">
        {articles.length === 0 ? (
          <div className="border border-dashed border-[#E7E5E4] py-20 text-center">
            <p className="font-display text-2xl italic text-[#57534E]">Questa raccolta sta ancora crescendo. Torna presto!</p>
          </div>
        ) : (
          <>
            {/* Da dove iniziare — most read */}
            {featured && rest.length > 0 && (
              <div className="mb-16 border-t border-[#E7E5E4] pt-12">
                <p className="inline-flex items-center gap-2 label-ui text-[#9F583F] mb-6">
                  <Star size={14} strokeWidth={1.6} /> Da dove iniziare
                </p>
                <div className="max-w-2xl">
                  <ArticleCard p={featured} featured />
                </div>
              </div>
            )}

            <div className="border-t border-[#E7E5E4] pt-12">
              <h2 className="font-display text-3xl tracking-tight text-[#1C1917] mb-10">Tutti gli articoli</h2>
              <div className="grid md:grid-cols-3 gap-x-8 gap-y-12">
                {(rest.length > 0 ? rest : articles).map((p, i) => (
                  <motion.div
                    key={p.slug}
                    initial={{ opacity: 0, y: 22 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.5, delay: (i % 3) * 0.08, ease: "easeOut" }}
                  >
                    <ArticleCard p={p} />
                  </motion.div>
                ))}
              </div>
            </div>
          </>
        )}
      </section>

      <Footer />
    </div>
  );
}
